import { Injectable } from '@angular/core';
import { AppConfig } from '../../app/app.config';
import { PageValidationType } from '../../models/projects/validation.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { NavHelperService } from './nav-helper.service';
import { menuSectionFor, menuSections } from './menu-sections';
import { PermissionsService } from '../permissions/permissions.service';
import { VesselService } from '../vessel/vessel.service';
import { ImplementerService } from '../implementer/implementer.service';
import { DailyLogService } from '../daily-log/daily-log.service';

@Injectable({ providedIn: 'root' })
export class SectionNavigationService {
  constructor(private navHelper: NavHelperService,
              private permissions: PermissionsService,
              private vessel: VesselService,
              private implementer: ImplementerService,
              private dailyLog: DailyLogService) {}

  async availableSections(): Promise<PageValidationType[]> {
    const config = AppConfig.settings;
    const [permissions, nonJacking, snapshot] = await Promise.all([
      this.permissions.sidebarPermissions(),
      this.vessel.isCurrentVesselNonJacking(),
      config.isImplementerEnvironment ? this.implementer.getActiveSnapshotSummary(config.isVesselEnvironment) : Promise.resolve(null)
    ]);
    return menuSections.filter(section => this.navHelper.canOpenMenuSection(section, permissions, nonJacking, !!snapshot))
      .map(section => section.pageType);
  }

  async open(pageType: PageValidationType, validationDay: PtLuxonDate): Promise<string | null> {
    const expectedDay = validationDay?.dateTime?.toMillis();
    if (!Number.isFinite(expectedDay)) {
      return 'The reporting day is unavailable. Reopen validation from the required reporting day.';
    }
    const section = menuSectionFor(pageType);
    if (!section || !(await this.availableSections()).includes(pageType)) {
      return 'This section is not available through the application menu.';
    }

    // Use exactly the day the destination will load, without changing shared date state.
    const selectedDay = await this.dailyLog.getCurrentLogDay(false, true);
    const verifiedDay = await this.dailyLog.getCurrentLogDay(false);
    const currentSelection = await this.dailyLog.getCurrentLogDay(false, true);
    if (selectedDay?.dateTime?.toMillis() !== expectedDay
        || verifiedDay?.dateTime?.toMillis() !== expectedDay
        || currentSelection?.dateTime?.toMillis() !== expectedDay) {
      return 'The selected reporting day no longer matches these validation results. Select the required day in the Daily Log and reopen validation.';
    }

    this.navHelper.navigateFromMenu({ page: section.component,
      params: section.requiresDaySetup ? { type: undefined } : undefined,
      supportContextKey: section.supportContextKey, supportContextLabel: section.title });
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/services/nav/section-navigation.service.ts